// El presupuesto de los editores con ciclo de vida: tope en pesos y, con tope, la barra de consumo.
// Recibe y avisa texto, así sirve igual con estado propio que con un `Controller`.
import { useId, useState, type ReactNode, type Ref } from 'react';
import { BudgetBar } from '@/shared/patterns/BudgetBar';
import { ClearableField, FieldHelp, FieldMessage } from '@/shared/patterns/EditorForm';
import { LIFECYCLE_FIELD_HELP } from './helpText';

const LABEL = 'Presupuesto tope';
const NOTE = 'mt-1 text-[10px] text-muted-foreground';

const wholePesos = new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
});
const withCents = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });

function formatBudgetCents(cents: number): string {
    return (cents % 100 === 0 ? wholePesos : withCents).format(cents / 100);
}

export interface BudgetFieldProps {
    /** Lo tecleado, en pesos. */
    value: string;
    onChange: (text: string) => void;
    onBlur?: () => void;
    inputRef?: Ref<HTMLInputElement>;
    /** El tope vigente en centavos; `null` = sin tope. */
    maxCents: number | null;
    /** Lo gastado en centavos; `null` mientras la promo no existe. */
    consumedCents: number | null;
    help?: string;
    /** Nota propia del dominio bajo la barra. */
    hint?: ReactNode;
    error?: string;
    disabled?: boolean;
    id?: string;
}

export function BudgetField({
    value,
    onChange,
    onBlur,
    inputRef,
    maxCents,
    consumedCents,
    help = LIFECYCLE_FIELD_HELP.budgetMax,
    hint,
    error,
    disabled,
    id,
}: BudgetFieldProps) {
    const fallbackId = useId();
    const inputId = id ?? fallbackId;
    // Lo tecleado manda mientras hay foco: así «20.» no se reformatea a «20» a media escritura.
    const [typed, setTyped] = useState<string | null>(null);
    const shown = typed ?? value;
    const capped = maxCents !== null && maxCents > 0;
    return (
        <div className="rounded-md border border-border p-2.5">
            <div className="mb-1.5 flex items-center gap-1">
                <label
                    htmlFor={inputId}
                    className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground"
                >
                    {LABEL}
                </label>
                <FieldHelp label={LABEL} help={help} />
            </div>
            <div className="flex items-center gap-2">
                <span className="block w-[130px] flex-none">
                    <ClearableField
                        label="el presupuesto tope"
                        filled={shown !== ''}
                        disabled={disabled}
                        onClear={() => {
                            setTyped(null);
                            onChange('');
                        }}
                    >
                        <input
                            ref={inputRef}
                            id={inputId}
                            type="text"
                            inputMode="decimal"
                            placeholder="Sin tope"
                            disabled={disabled}
                            aria-invalid={error ? true : undefined}
                            value={shown}
                            onChange={(event) => {
                                setTyped(event.target.value);
                                onChange(event.target.value);
                            }}
                            onBlur={() => {
                                setTyped(null);
                                onBlur?.();
                            }}
                            className="w-full rounded border border-border bg-background py-[5px] pl-2 pr-6 text-xs tabular-nums text-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/15 disabled:opacity-60 aria-[invalid=true]:border-destructive"
                        />
                    </ClearableField>
                </span>
                <span className="whitespace-nowrap text-[11px] text-muted-foreground">$ MXN</span>
            </div>
            <FieldMessage message={error} />
            {capped && consumedCents !== null ? (
                <BudgetBar
                    label="Presupuesto consumido"
                    consumed={consumedCents}
                    max={maxCents}
                    start={`Consumido ${formatBudgetCents(consumedCents)} de ${formatBudgetCents(maxCents)}`}
                />
            ) : null}
            <p className={NOTE}>
                {capped
                    ? 'Se pausa al gastarlo.'
                    : `Sin tope${consumedCents === null ? '' : ` · van ${formatBudgetCents(consumedCents)}`}`}
            </p>
            {hint ? <p className={NOTE}>{hint}</p> : null}
        </div>
    );
}
